import React, { useState } from "react";
import { profile, contactContent } from "../../data/content";
import { Copy, Check, Phone, GithubLogo, LinkedinLogo, ArrowUp, MapPin, Clock, PaperPlaneTilt } from "@phosphor-icons/react";
import { EdgeTelemetryConsole } from "../contact/EdgeTelemetryConsole";

export const ContactSection: React.FC = () => {
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(profile.email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer id="contact" className="pt-20 sm:pt-24 pb-12 sm:pb-16 scroll-mt-20 sm:scroll-mt-24">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        {/* Contact Header */}
        <div className="mb-10 sm:mb-12 border-b border-[#242320] pb-8 text-left">
          <div className="flex items-center gap-2 mb-2">
            <PaperPlaneTilt className="w-4 h-4 text-[#E5983A]" weight="bold" />
            <span className="text-xs font-mono uppercase tracking-widest text-[#E5983A]">
              {contactContent.sectionTag}
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold font-display tracking-tight text-[#F3F2EE]">
            {contactContent.title}
          </h2>
        </div>

        {/* Contact Content Symmetrical Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-16 text-left items-stretch">
          {/* Left Column: Direct Links & Bio Channels */}
          <div className="flex flex-col justify-between space-y-6">
            {/* Primary Direct Email Card */}
            <div className="p-6 sm:p-7 rounded-2xl bg-[#181716] border border-[#2A2926] space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-[#8C8A84] uppercase tracking-wider">
                  Direct Email
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#4ADE80]/10 border border-[#4ADE80]/20 text-[11px] font-mono text-[#4ADE80]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#4ADE80] animate-pulse" />
                  {profile.availability}
                </span>
              </div>

              <div className="flex items-center justify-between gap-3 pt-1">
                <a
                  id="contact-email-link"
                  href={`mailto:${profile.email}?subject=Contract%20Inquiry%20%E2%80%94%20Mohammad%20Hosein%20Roustaie`}
                  className="font-mono text-lg sm:text-xl font-medium text-[#F3F2EE] hover:text-[#E5983A] underline decoration-[#666666]/60 hover:decoration-[#E5983A] underline-offset-6 transition-all truncate"
                >
                  {profile.email}
                </a>
                <button
                  id="contact-copy-email-btn"
                  onClick={handleCopyEmail}
                  className="p-2 rounded-xl bg-[#201F1D] border border-[#2D2B27] text-[#C8C6BE] hover:text-[#E5983A] hover:border-[#E5983A]/40 transition-all shrink-0 cursor-pointer"
                  title="Copy email to clipboard"
                  aria-label="Copy email address"
                >
                  {copied ? <Check size={16} weight="bold" className="text-[#4ADE80]" /> : <Copy size={16} />}
                </button>
              </div>

              {copied && (
                <span className="text-xs font-mono text-[#4ADE80] block">
                  ✓ Copied to clipboard!
                </span>
              )}

              <p className="text-xs font-mono text-[#8C8A84] leading-relaxed pt-2 border-t border-[#242320]">
                {contactContent.directEmailNote}
              </p>
            </div>

            {/* Channels & Location Card */}
            <div className="p-6 sm:p-7 rounded-2xl bg-[#181716] border border-[#2A2926] space-y-3.5">
              <span className="text-xs font-mono text-[#8C8A84] uppercase tracking-wider block mb-1">
                Channels & Coordinates
              </span>

              <a
                href={profile.github}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between py-2 border-b border-[#242320] text-sm text-[#F3F2EE] hover:text-[#E5983A] transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <GithubLogo size={18} />
                  <span>GitHub</span>
                </div>
                <span className="text-xs font-mono text-[#8C8A84]">/{profile.githubHandle}</span>
              </a>

              <a
                href={profile.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between py-2 border-b border-[#242320] text-sm text-[#F3F2EE] hover:text-[#E5983A] transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <LinkedinLogo size={18} />
                  <span>LinkedIn</span>
                </div>
                <span className="text-xs font-mono text-[#8C8A84]">/{profile.linkedinHandle}</span>
              </a>

              <a
                href={`tel:${profile.phone}`}
                className="flex items-center justify-between py-2 border-b border-[#242320] text-sm text-[#F3F2EE] hover:text-[#E5983A] transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <Phone size={18} />
                  <span>Phone</span>
                </div>
                <span className="text-xs font-mono text-[#8C8A84]">{profile.phoneDisplay}</span>
              </a>

              <div className="flex items-center justify-between pt-1 text-xs font-mono text-[#8C8A84]">
                <span className="flex items-center gap-1.5">
                  <MapPin size={14} className="text-[#E5983A]" />
                  <span>{profile.location}</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock size={14} className="text-[#E5983A]" />
                  <span>{profile.timezoneDisplay}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Right Column: Graphical Edge Telemetry Console */}
          <div className="h-full">
            <EdgeTelemetryConsole />
          </div>
        </div>

        {/* Clean Footer Bar */}
        <div className="pt-8 border-t border-[#242320] flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-mono text-[#8C8A84]">
          <p>© 2026 {profile.name} · Tehran, Iran</p>
          <button
            onClick={scrollToTop}
            className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-[#1C1B19] hover:bg-[#252422] border border-[#2B2A27] text-[#DCDAD2] hover:text-[#E5983A] transition group cursor-pointer"
          >
            <span>Back to top</span>
            <ArrowUp size={13} weight="bold" className="group-hover:-translate-y-0.5 transition-transform" />
          </button>
        </div>
      </div>
    </footer>
  );
};
